import React, { useState, useEffect } from 'react';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	CheckCircleIcon,
	DocumentIcon,
	XMarkIcon,
	PencilSquareIcon,
	ForwardIcon,
	PrinterIcon,
	ArrowLeftIcon,
	DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { StackableModal, StatusBadge } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';
import MarkdownOrHtmlDisplay from '../components/MarkdownOrHtmlDisplay';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'sopViewer';

const SopViewerModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	const sop = open?.sop;
	const steps = sop?.steps || [];
	const onComplete = open?.onComplete;
	// Accept initial values for pre-populating from saved responses
	const initialCompletedSteps = open?.initialCompletedSteps || {};
	const initialSkippedSteps = open?.initialSkippedSteps || {};
	const initialStepInputs = open?.stepInputs || {};
	// When readOnly is true, show summary only and don't allow step navigation
	const readOnly = open?.readOnly || false;

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [completedSteps, setCompletedSteps] = useState({});
	const [skippedSteps, setSkippedSteps] = useState({});
	const [stepInputs, setStepInputs] = useState({});
	const [viewingAttachment, setViewingAttachment] = useState(null);
	const [showSummary, setShowSummary] = useState(false);

	// Reset state when modal opens
	useEffect(() => {
		if (open && sop) {
			setCompletedSteps(initialCompletedSteps);
			setSkippedSteps(initialSkippedSteps);
			setStepInputs(initialStepInputs);

			// If readOnly (task completed/skipped), always show summary
			if (readOnly) {
				setShowSummary(true);
				setCurrentStepIndex(steps.length - 1);
				return;
			}

			// Find the first incomplete step (not completed and not skipped)
			const sopSteps = sop.steps || [];
			let startIndex = 0;
			let allDone = true;
			for (let i = 0; i < sopSteps.length; i++) {
				if (!initialCompletedSteps[i] && !initialSkippedSteps[i]) {
					startIndex = i;
					allDone = false;
					break;
				}
			}
			// If all steps are done, show summary
			if (allDone && sopSteps.length > 0) {
				setShowSummary(true);
				setCurrentStepIndex(sopSteps.length - 1);
			} else {
				setShowSummary(false);
				setCurrentStepIndex(startIndex);
			}
		}
	}, [open?.sop?.id]);

	const currentStep = steps[currentStepIndex];
	const totalSteps = steps.length;
	const isFirstStep = currentStepIndex === 0;
	const isLastStep = currentStepIndex === totalSteps - 1;
	const completedCount = Object.keys(completedSteps).filter(
		(k) => completedSteps[k],
	).length;
	const skippedCount = Object.keys(skippedSteps).filter(
		(k) => skippedSteps[k],
	).length;

	const handlePrevious = () => {
		if (!isFirstStep) {
			setCurrentStepIndex(currentStepIndex - 1);
		}
	};

	const handleNext = () => {
		if (!isLastStep) {
			setCurrentStepIndex(currentStepIndex + 1);
		}
	};

	// Check if all steps are completed or skipped
	const areAllStepsDone = (completed, skipped) => {
		for (let i = 0; i < steps.length; i++) {
			if (!completed[i] && !skipped[i]) {
				return false;
			}
		}
		return steps.length > 0;
	};

	const handleToggleComplete = () => {
		// If completing, remove from skipped and advance to next step
		if (!completedSteps[currentStepIndex]) {
			const newSkipped = {
				...skippedSteps,
				[currentStepIndex]: false,
			};
			const newCompleted = {
				...completedSteps,
				[currentStepIndex]: true,
			};
			setSkippedSteps(newSkipped);
			setCompletedSteps(newCompleted);

			// Check if all steps are now done
			if (areAllStepsDone(newCompleted, newSkipped)) {
				setShowSummary(true);
			} else if (currentStepIndex < steps.length - 1) {
				// Auto-advance to next step after marking complete
				setCurrentStepIndex(currentStepIndex + 1);
			}
		} else {
			// If uncompleting, just toggle off
			setCompletedSteps((prev) => ({
				...prev,
				[currentStepIndex]: false,
			}));
		}
	};

	const handleToggleSkip = () => {
		// If skipping, remove from completed and advance to next step
		if (!skippedSteps[currentStepIndex]) {
			const newCompleted = {
				...completedSteps,
				[currentStepIndex]: false,
			};
			const newSkipped = {
				...skippedSteps,
				[currentStepIndex]: true,
			};
			setCompletedSteps(newCompleted);
			setSkippedSteps(newSkipped);

			// Check if all steps are now done
			if (areAllStepsDone(newCompleted, newSkipped)) {
				setShowSummary(true);
			} else if (currentStepIndex < steps.length - 1) {
				// Auto-advance to next step after skipping
				setCurrentStepIndex(currentStepIndex + 1);
			}
		} else {
			// If unskipping, just toggle off
			setSkippedSteps((prev) => ({
				...prev,
				[currentStepIndex]: false,
			}));
		}
	};

	const handleInputChange = (value) => {
		setStepInputs((prev) => ({
			...prev,
			[currentStepIndex]: value,
		}));
	};

	const handleFinish = () => {
		if (onComplete) {
			onComplete({
				completedSteps,
				skippedSteps,
				stepInputs,
			});
		}
		setClose();
	};

	const handleClose = () => {
		setClose();
	};

	const isCurrentStepComplete = completedSteps[currentStepIndex];
	const isCurrentStepSkipped = skippedSteps[currentStepIndex];
	const currentInput = stepInputs[currentStepIndex] || '';

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			size="full"
			showCloseIcon={false}
		>
			{!sop ? (
				<div className="flex flex-col items-center justify-center py-12">
					<p className="text-gray-500">No SOP available</p>
					<button
						type="button"
						onClick={handleClose}
						className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
					>
						Close
					</button>
				</div>
			) : steps.length === 0 ? (
				<div className="flex flex-col h-full min-h-[80vh]">
					{/* Header for description-only view */}
					<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
						<div className="flex items-center gap-4">
							<button
								type="button"
								onClick={handleClose}
								className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
							<div>
								<h2 className="text-lg font-semibold text-gray-900">
									{sop.name}
								</h2>
								<p className="text-sm text-gray-500">Overview</p>
							</div>
						</div>
						<button
							type="button"
							onClick={handleClose}
							className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
						>
							Close
						</button>
					</div>

					{/* Description content */}
					<div className="flex-1 overflow-auto px-6 py-6">
						<div className="max-w-3xl mx-auto">
							{sop.description ? (
								<div className="bg-white border border-gray-200 rounded-lg p-6">
									<MarkdownOrHtmlDisplay
										content={sop.description}
										className="text-gray-700"
									/>
								</div>
							) : (
								<div className="text-center py-12">
									<p className="text-gray-500">
										No description or steps available for this SOP.
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			) : showSummary ? (
				<div className="flex flex-col h-full min-h-[80vh]">
					{/* Summary Header */}
					<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white print:border-none">
						<div className="flex items-center gap-4">
							{!readOnly && (
								<button
									type="button"
									onClick={() => setShowSummary(false)}
									className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
								>
									<ArrowLeftIcon className="h-5 w-5" />
								</button>
							)}
							<div>
								<h2 className="text-lg font-semibold text-gray-900">
									{sop.name} - Summary
								</h2>
								<p className="text-sm text-gray-500">
									{completedCount} completed, {skippedCount} skipped
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2 print:hidden">
							<button
								type="button"
								onClick={() => window.print()}
								className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
							>
								<PrinterIcon className="h-4 w-4" />
								Print
							</button>
							<button
								type="button"
								onClick={readOnly ? handleClose : handleFinish}
								className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
							>
								{readOnly ? 'Close' : 'Done'}
							</button>
						</div>
					</div>

					{/* Summary Content */}
					<div className="flex-1 overflow-auto px-6 py-6">
						<div className="max-w-3xl mx-auto space-y-4">
							{areAllStepsDone(completedSteps, skippedSteps) ? (
								<div className="text-center mb-8 print:mb-4">
									<CheckCircleSolidIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
									<h3 className="text-xl font-semibold text-gray-900">
										SOP Completed
									</h3>
									<p className="text-gray-500 mt-1">
										Completed on {new Date().toLocaleDateString()} at{' '}
										{new Date().toLocaleTimeString()}
									</p>
								</div>
							) : (
								<div className="text-center mb-8 print:mb-4">
									<div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
										<span className="text-2xl font-bold text-amber-600">
											{completedCount + skippedCount}/{steps.length}
										</span>
									</div>
									<h3 className="text-xl font-semibold text-gray-900">
										SOP Incomplete
									</h3>
									<p className="text-gray-500 mt-1">
										Please go back and complete all remaining steps.
									</p>
								</div>
							)}

							{steps.map((step, index) => (
								<div
									key={index}
									className={classNames(
										'border rounded-lg p-4',
										completedSteps[index]
											? 'border-gray-200'
											: skippedSteps[index]
												? 'border-amber-200 bg-amber-50'
												: 'border-gray-200 bg-gray-50',
									)}
								>
									<div className="flex items-start gap-3">
										<span
											className={classNames(
												'inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold flex-shrink-0',
												completedSteps[index]
													? 'bg-green-500 text-white'
													: skippedSteps[index]
														? 'bg-amber-400 text-white'
														: 'bg-gray-300 text-gray-700',
											)}
										>
											{index + 1}
										</span>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2 mb-2">
												<span className="font-medium text-gray-900">
													Step {index + 1}
												</span>
												{completedSteps[index] && (
													<StatusBadge status="completed" />
												)}
												{skippedSteps[index] && (
													<StatusBadge status="skipped" />
												)}
											</div>
											<MarkdownOrHtmlDisplay
												content={step.content}
												className="text-gray-600 mb-2"
											/>
											{stepInputs[index] && (
												<div className="mt-3 p-3 bg-white border border-gray-200 rounded-md">
													<p className="text-xs font-medium text-gray-500 mb-1">
														User Response:
													</p>
													<p className="text-sm text-gray-700 whitespace-pre-wrap">
														{stepInputs[index]}
													</p>
												</div>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Summary Footer */}
					<div
						className={classNames(
							'flex items-center px-6 py-4 border-t border-gray-200 bg-white print:hidden',
							readOnly ? 'justify-end' : 'justify-between',
						)}
					>
						{!readOnly && (
							<button
								type="button"
								onClick={() => setShowSummary(false)}
								className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
							>
								<ArrowLeftIcon className="h-4 w-4" />
								Back to Steps
							</button>
						)}
						<button
							type="button"
							onClick={readOnly ? handleClose : handleFinish}
							className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
						>
							{readOnly ? 'Close' : 'Save & Close'}
						</button>
					</div>
				</div>
			) : (
				<div className="flex flex-col h-full min-h-[80vh]">
					{/* Header */}
					<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
						<div className="flex items-center gap-4">
							<button
								type="button"
								onClick={handleClose}
								className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
							<div>
								<h2 className="text-lg font-semibold text-gray-900">
									{sop.name}
								</h2>
								<p className="text-sm text-gray-500">
									{sop.versionMajor !== undefined &&
										sop.versionMinor !== undefined && (
											<span>
												Version {sop.versionMajor}.{sop.versionMinor}
											</span>
										)}
									{sop.isLockedVersion && (
										<span className="ml-2 text-amber-600">(Locked)</span>
									)}
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={handleFinish}
								className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
							>
								Done
							</button>
						</div>
					</div>

					{/* Two-column layout */}
					<div className="flex-1 flex overflow-hidden">
						{/* Left column - Description */}
						<div className="w-1/3 border-r border-gray-200 bg-gray-50 overflow-y-auto">
							<div className="p-6">
								{/* SOP Info */}
								<div className="mb-6">
									<div className="flex items-center gap-2 mb-3">
										<DocumentTextIcon className="h-5 w-5 text-indigo-600" />
										<h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
											Overview
										</h3>
									</div>
									{sop.description ? (
										<div className="bg-white border border-gray-200 rounded-lg p-4">
											<MarkdownOrHtmlDisplay
												content={sop.description}
												className="text-gray-700 text-sm"
											/>
										</div>
									) : (
										<p className="text-sm text-gray-500 italic">
											No description provided.
										</p>
									)}
								</div>

								{/* Progress summary */}
								<div className="mb-6">
									<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
										Progress
									</h4>
									<div className="bg-white border border-gray-200 rounded-lg p-4">
										<div className="flex items-center justify-between mb-2">
											<span className="text-sm text-gray-600">Completed</span>
											<span className="text-sm font-medium text-green-600">
												{completedCount} / {totalSteps}
											</span>
										</div>
										{skippedCount > 0 && (
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600">Skipped</span>
												<span className="text-sm font-medium text-amber-600">
													{skippedCount}
												</span>
											</div>
										)}
										<div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
											<div
												className="h-full bg-green-500 transition-all duration-300"
												style={{
													width: `${((completedCount + skippedCount) / totalSteps) * 100}%`,
												}}
											/>
										</div>
									</div>
								</div>

								{/* Steps list */}
								<div>
									<h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
										Steps
									</h4>
									<div className="space-y-2">
										{steps.map((step, index) => (
											<button
												key={index}
												type="button"
												onClick={() => setCurrentStepIndex(index)}
												className={classNames(
													'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
													index === currentStepIndex
														? 'bg-indigo-100 border border-indigo-300'
														: 'bg-white border border-gray-200 hover:border-gray-300',
												)}
											>
												<span
													className={classNames(
														'inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold flex-shrink-0',
														completedSteps[index]
															? 'bg-green-500 text-white'
															: skippedSteps[index]
																? 'bg-amber-400 text-white'
																: index === currentStepIndex
																	? 'bg-indigo-600 text-white'
																	: 'bg-gray-200 text-gray-600',
													)}
												>
													{completedSteps[index] ? (
														<CheckCircleIcon className="h-4 w-4" />
													) : (
														index + 1
													)}
												</span>
												<span
													className={classNames(
														'text-sm truncate',
														index === currentStepIndex
															? 'text-indigo-900 font-medium'
															: 'text-gray-700',
													)}
												>
													{(() => {
														// Strip HTML tags and get plain text
														const plainText = (step.content || '')
															.replace(/<[^>]*>/g, '')
															.replace(/[#*_~`]/g, '')
															.trim();
														// Get first line or first 100 chars, whichever is shorter
														const firstLine = plainText.split('\n')[0];
														const truncated = firstLine.length > 100
															? firstLine.slice(0, 100) + '...'
															: firstLine;
														return truncated || `Step ${index + 1}`;
													})()}
												</span>
											</button>
										))}
									</div>
								</div>
							</div>
						</div>

						{/* Right column - Current Step */}
						<div className="flex-1 flex flex-col overflow-hidden">
							{/* Step content area */}
							<div className="flex-1 overflow-auto px-6 py-6">
								<div className="max-w-2xl mx-auto">
									{/* Step header */}
									<div className="flex items-center justify-between mb-6">
										<div className="flex items-center gap-3">
											<span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100 text-indigo-700 font-semibold">
												{currentStepIndex + 1}
											</span>
											<div>
												<h3 className="text-xl font-medium text-gray-900">
													Step {currentStepIndex + 1}
												</h3>
												<p className="text-sm text-gray-500">
													of {totalSteps} steps
												</p>
											</div>
										</div>
										{currentStep.requiresInput && (
											<span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
												<PencilSquareIcon className="w-3.5 h-3.5" />
												Input Required
											</span>
										)}
									</div>

									{/* Step content */}
									<div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
										<MarkdownOrHtmlDisplay
											content={currentStep.content}
											className="text-gray-700"
										/>
									</div>

									{/* Attachments */}
									{currentStep.attachments?.length > 0 && (
										<div className="mb-6">
											<h4 className="text-sm font-medium text-gray-700 mb-3">
												Attachments
											</h4>
											<div className="grid grid-cols-2 gap-3">
												{currentStep.attachments.map((attachment, idx) => (
													<button
														key={idx}
														type="button"
														onClick={() => setViewingAttachment(attachment)}
														className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors text-left"
													>
														<DocumentIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
														<div className="min-w-0">
															<p className="text-sm font-medium text-gray-900 truncate">
																{attachment.name}
															</p>
															{attachment.size && (
																<p className="text-xs text-gray-500">
																	{Math.round(parseInt(attachment.size) / 1024)}{' '}
																	KB
																</p>
															)}
														</div>
													</button>
												))}
											</div>
										</div>
									)}

									{/* User input field */}
									{currentStep.requiresInput && (
										<div className="mb-6">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Your Response
											</label>
											<textarea
												value={currentInput}
												onChange={(e) => handleInputChange(e.target.value)}
												placeholder="Enter your response for this step..."
												rows={4}
												className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
											/>
										</div>
									)}
								</div>
							</div>

							{/* Footer navigation */}
							<div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
								<button
									type="button"
									onClick={handlePrevious}
									disabled={isFirstStep}
									className={classNames(
										'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
										isFirstStep
											? 'text-gray-400 cursor-not-allowed'
											: 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50',
									)}
								>
									<ChevronLeftIcon className="h-4 w-4" />
									Previous
								</button>

								<div className="flex items-center gap-2">
									{!currentStep?.requiresInput && !isCurrentStepComplete && (
										<button
											type="button"
											onClick={handleToggleSkip}
											className={classNames(
												'inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
												isCurrentStepSkipped
													? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
													: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
											)}
										>
											{isCurrentStepSkipped ? 'Skipped' : 'Skip'}
										</button>
									)}

									<button
										type="button"
										onClick={
											isCurrentStepComplete
												? isLastStep
													? () => setShowSummary(true)
													: handleNext
												: handleToggleComplete
										}
										disabled={
											!isCurrentStepComplete &&
											currentStep?.requiresInput &&
											!currentInput.trim()
										}
										className={classNames(
											'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
											!isCurrentStepComplete &&
												currentStep?.requiresInput &&
												!currentInput.trim()
												? 'text-gray-400 bg-gray-100 cursor-not-allowed'
												: 'text-white bg-indigo-600 hover:bg-indigo-700',
										)}
									>
										{isCurrentStepComplete ? (
											<>
												{isLastStep ? 'View Summary' : 'Next'}
												<ChevronRightIcon className="h-4 w-4" />
											</>
										) : (
											'Mark Completed'
										)}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Attachment viewer modal */}
			<StackableModal
				open={!!viewingAttachment}
				onClose={() => setViewingAttachment(null)}
				size="4xl"
			>
				{viewingAttachment && (
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-medium text-gray-900">
								{viewingAttachment.name}
							</h3>
							<a
								href={viewingAttachment.url}
								target="_blank"
								rel="noopener noreferrer"
								className="text-sm text-indigo-600 hover:text-indigo-700"
							>
								Open in new tab
							</a>
						</div>
						<div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
							{viewingAttachment.type?.startsWith('image/') ? (
								<img
									src={viewingAttachment.url}
									alt={viewingAttachment.name}
									className="max-w-full h-auto mx-auto"
								/>
							) : viewingAttachment.type === 'application/pdf' ? (
								<iframe
									src={viewingAttachment.url}
									title={viewingAttachment.name}
									className="w-full h-[70vh]"
								/>
							) : (
								<div className="flex flex-col items-center justify-center py-12">
									<DocumentIcon className="h-16 w-16 text-gray-400 mb-4" />
									<p className="text-sm text-gray-500 mb-4">
										Preview not available for this file type
									</p>
									<a
										href={viewingAttachment.url}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
									>
										Download File
									</a>
								</div>
							)}
						</div>
						<div className="flex justify-end">
							<button
								type="button"
								onClick={() => setViewingAttachment(null)}
								className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
							>
								Close
							</button>
						</div>
					</div>
				)}
			</StackableModal>
		</StackableModal>
	);
};

export default SopViewerModal;
